/**
 * Client-side helpers for the recipe VIDEO import upload flow (video-import,
 * Slice 4 — `VideoImportButton`). Mirrors `imageUpload.ts`'s shape, but for
 * video: there is no client-side resize/re-encode (video isn't
 * canvas-encodable in the browser) — a file is either accepted as-is or
 * rejected before ever attempting an upload.
 *
 *  - `ALLOWED_VIDEO_TYPES` — mirrors the API's own `ALLOWED_VIDEO_CONTENT_TYPES`
 *    allow-list (`storage.service.ts`).
 *  - `MAX_VIDEO_BYTES` — mirrors the API's own cap (`MAX_VIDEO_BYTES` in
 *    `recipe-video-import/video-source-resolver.ts`, 50 MB) so an oversized
 *    file is rejected client-side with an immediate, clear message instead of
 *    burning an upload round-trip only to fail server-side.
 *  - `resolveVideoContentType` — a browser can report a blank or unexpected
 *    `file.type` for some video files (observed for `.mov` on some Windows
 *    browsers); falls back to a filename-extension map (mirroring the API's
 *    own extension mapping) before giving up.
 *  - `validateVideoFile` — reject (before any upload attempt) a file whose
 *    resolved content type isn't on the allow-list, or that's over the size
 *    cap.
 */

/** MIME types the API's video upload-url endpoint accepts (mirrors
 * `ALLOWED_VIDEO_CONTENT_TYPES` in `storage.service.ts`). */
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"] as const;
export type AllowedVideoType = (typeof ALLOWED_VIDEO_TYPES)[number];

/** Reject anything picked over this size, before even attempting an upload —
 * mirrors the API's own `MAX_VIDEO_BYTES` cap (50 MB). */
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

/** Filename-extension fallback, for a browser that reports a blank/wrong
 * `file.type` for a video file. Mirrors the API's own extension mapping. */
const VIDEO_EXTENSION_CONTENT_TYPES: Record<string, AllowedVideoType> = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};

/**
 * Resolve a picked file's upload content type: the browser-reported
 * `file.type` if it's on the allow-list, else a best-effort guess from the
 * filename's extension, else `null` (unsupported / unrecognizable).
 */
export function resolveVideoContentType(file: File): AllowedVideoType | null {
  if ((ALLOWED_VIDEO_TYPES as readonly string[]).includes(file.type)) {
    return file.type as AllowedVideoType;
  }
  const ext = file.name.split(".").pop()?.toLowerCase();
  return (ext && VIDEO_EXTENSION_CONTENT_TYPES[ext]) || null;
}

/**
 * Validate a picked video file BEFORE attempting any upload. Returns a short
 * Norwegian error message on failure, or `null` when the file is acceptable.
 */
export function validateVideoFile(file: File): string | null {
  if (!resolveVideoContentType(file)) {
    return "Filtypen støttes ikke. Bruk MP4, MOV eller WebM.";
  }
  if (file.size > MAX_VIDEO_BYTES) {
    return "Videoen er for stor (maks 50 MB).";
  }
  return null;
}
